import { Lock } from 'lucide-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import { IndexPath } from '@/Config/app/router/index.path';
import { useCurrentIndexPath } from '@/Config/app/router/useIndexPath.hook';
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/Domain/components/Shared/Sidebar/Sidebar';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { ConnectIcon, ConnectIconHandle } from '@/Domain/components/icons/ConnectIcon';
import {
  MonitorCheckIcon,
  MonitorCheckIconHandle,
} from '@/Domain/components/icons/MonitorCheckIcon';
import { cn } from '@/Domain/utils/cn';

export const SidebarChannelsGroup = ({ isLocked }: { isLocked: boolean }) => {
  const { t } = useTranslation('sidebar_menu');
  const currentPath = useCurrentIndexPath();
  const websiteIconRef = useRef<MonitorCheckIconHandle>(null);
  const externalAppsIconRef = useRef<ConnectIconHandle>(null);

  const content = (
    <SidebarGroup>
      <SidebarGroupLabel>
        {t('menu.channels.title')}
        {isLocked && <Lock className="ml-auto !size-3" />}
      </SidebarGroupLabel>
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            asChild
            tooltip={t('menu.channels.website')}
            isActive={currentPath == IndexPath.website}
            className={cn(isLocked && 'opacity-50 pointer-events-none')}
            onMouseEnter={() => !isLocked && websiteIconRef.current?.startAnimation()}
            onMouseLeave={() => !isLocked && websiteIconRef.current?.stopAnimation()}
          >
            {isLocked ? (
              <div>
                <MonitorCheckIcon size={16} />
                <span>{t('menu.channels.website')}</span>
              </div>
            ) : (
              <NavLink to={IndexPath.website} data-shortcut="7" viewTransition>
                <MonitorCheckIcon ref={websiteIconRef} size={16} />
                <span>{t('menu.channels.website')}</span>
              </NavLink>
            )}
          </SidebarMenuButton>
        </SidebarMenuItem>
        <SidebarMenuItem>
          <SidebarMenuButton
            asChild
            tooltip={t('menu.channels.external_apps')}
            isActive={currentPath == IndexPath.channels}
            className={cn(isLocked && 'opacity-50 pointer-events-none')}
            onMouseEnter={() => !isLocked && externalAppsIconRef.current?.startAnimation()}
            onMouseLeave={() => !isLocked && externalAppsIconRef.current?.stopAnimation()}
          >
            {isLocked ? (
              <div>
                <ConnectIcon size={16} />
                <span>{t('menu.channels.external_apps')}</span>
              </div>
            ) : (
              <NavLink to={IndexPath.channels} data-shortcut="8" viewTransition>
                <ConnectIcon ref={externalAppsIconRef} size={16} />
                <span>{t('menu.channels.external_apps')}</span>
              </NavLink>
            )}
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarGroup>
  );

  if (isLocked) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div>{content}</div>
          </TooltipTrigger>
          <TooltipContent side="right">{t('tooltips.locked_section')}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return content;
};
